import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getUnitData } from '/business/shared/state/actions/application';
import { getEmissionTypes } from '/business/shared/state/actions/emissionTypes';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import getApplicationCountryCodeSelector from '/business/shared/state/selectors/application/model/getApplicationCountryCodeSelector';
import hasUnitSelector from '/business/shared/state/selectors/units/hasUnitSelector';
import getSessionTokenSelector from '/shared/state/selectors/getSessionTokenSelector';
import isLoadingSelector from '/shared/state/selectors/isLoadingSelector';
import useSelectorMemoized from '/business/shared/hooks/useSelectorMemoized';
import useHasChanged from '/business/shared/hooks/useHasChanged';
import type { LegacyDispatch } from '/business/shared/state/actions/types';
import type { RootState } from '/business/shared/state/store';

export function FetchApplicationMeta() {
  const dispatch = useDispatch<LegacyDispatch>();
  const applicationId = useSelector(getApplicationIdSelector) || null;
  const token = useSelector(getSessionTokenSelector) || null;
  const emissionTypes = useSelector((state: RootState) => state.emissionTypes?.data) || null;
  const isLoadingEmissions =
    useSelectorMemoized((state: RootState) => isLoadingSelector(state, 'emissionTypes')) || false;
  const countryCode = useSelector(getApplicationCountryCodeSelector) || null;
  const hasUnitProps = useSelector(hasUnitSelector) || false;
  const isLoadingUnitData = useSelector((state: RootState) => isLoadingSelector(state, 'unitData')) || false;
  const isLoadingHasChanged = useHasChanged(isLoadingUnitData);
  const isLoadingEmissionsHasChanged = useHasChanged(isLoadingEmissions);

  useEffect(() => {
    if (!applicationId || isLoadingEmissionsHasChanged) {
      return;
    }
    if (!isLoadingEmissions && !emissionTypes) {
      dispatch(getEmissionTypes(countryCode || undefined));
    }
  }, [dispatch, emissionTypes, isLoadingEmissions, countryCode, isLoadingEmissionsHasChanged, applicationId]);

  useEffect(() => {
    if (token) {
      if (!hasUnitProps && !isLoadingUnitData && applicationId && !isLoadingHasChanged) {
        dispatch(getUnitData(applicationId));
      }
    }
  }, [dispatch, token, hasUnitProps, isLoadingUnitData, applicationId, isLoadingHasChanged]);

  return null;
}

export default FetchApplicationMeta;
